import { LocalizationService } from '@abp/ng.core';
import { inject, Injectable } from '@angular/core';
import { DocRole } from '@digiwall/goliath-core/proxy';
import { DocHierarchyDto } from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types';

@Injectable({ providedIn: 'root' })
export class CustomerProjectHierarchyStatusService {
  private readonly enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  private readonly localizationService = inject(LocalizationService);

  /**
   * Status label shown for one project hierarchy row.
   *
   * A row carries the root document's docRole, but its status comes from the first not-closed
   * document of the tree (CustomerProjectAppService.FindFirstNotClosed), which is usually another
   * document with another role. Status ids are scoped to their category - 'Open' exists in
   * QuoteStatus, SupplierQuoteStatus, SupplierDeliveryStatus, ... - so an id only resolves under
   * the role of the document it came from, which is why that walk is repeated here instead of
   * pairing statusGlobalId with the row's own docRole.
   */
  getStatusLabel(hierarchy: DocHierarchyDto): string {
    const notClosed = this.findFirstNotClosed(hierarchy);

    if (notClosed == null) {
      return undefined;
    }

    return this.getStatus(notClosed.docStatusId, notClosed.docRole);
  }

  private findFirstNotClosed(item: DocHierarchyDto): DocHierarchyDto {
    if (!item.isDocClosed) {
      return item;
    }

    const children = [
      ...(item.previousDocs ?? []),
      ...(item.nextDocs ?? []),
      ...(item.branchDocs ?? []),
    ];

    for (const child of children) {
      const notClosed = this.findFirstNotClosed(child);

      if (notClosed != null) {
        return notClosed;
      }
    }

    return undefined;
  }

  private getStatus(statusId: string, role: DocRole): string {
    switch (role) {
      case DocRole.CustomerQuote:
        return this.enumerationTypeStore.quoteStatus().find(x => x.id === statusId)?.name?.current;
      case DocRole.CustomerSalesPurchase:
        return this.enumerationTypeStore.purchaseOrderStatus().find(x => x.id === statusId)?.name
          ?.current;
      case DocRole.CustomerDeliveryNote:
        return this.enumerationTypeStore.deliveryNoteStatus().find(x => x.id === statusId)?.name
          ?.current;
      case DocRole.CustomerInvoice:
        return this.enumerationTypeStore.invoicedStatus().find(x => x.id === statusId)?.name
          ?.current;
      case DocRole.CustomerReturnRequest:
        return this.enumerationTypeStore.customerReturnRequestStatus().find(x => x.id === statusId)
          ?.name?.current;
      case DocRole.CustomerReturnNote:
      case DocRole.CustomerCreditNote:
        //temporary
        return this.localizationService.instant('Doc::CustomerProject:InProgress');
      default:
        return undefined;
    }
  }
}
